import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ChevronRight, Flame, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AnnouncementBar from "@/components/sections/AnnouncementBar";
import ServiceFinder from "@/components/ServiceFinder";
import logoWhite from "@/assets/logo-white.png";
import ThemeToggle from "@/components/ThemeToggle";
import { cn } from "@/lib/utils";

// 4 High-Intent Navigation Pillars
const webDesignItems = [
  { to: "/services/custom-business-solutions", label: "Custom Business Platforms", description: "Bespoke operating systems & custom software" },
  { to: "/services/seo", label: "Local SEO Architecture", description: "Multi-page regional rank & search dominance" },
  { to: "/services/ecommerce", label: "E-Commerce", description: "Direct-to-consumer & wholesale sales engines" },
  { to: "/services/app-development", label: "Web & Mobile Apps", description: "iOS, Android & progressive web applications" },
  { to: "/services/web-design", label: "Stop Renting Software", description: "100% owned platforms with zero monthly SaaS rent" },
];

const marketingItems = [
  { to: "/services/ppc", label: "Google Call Ads", description: "High-intent inbound phone calls that ring your line" },
  { to: "/services/local-service-ads", label: "Local Service Ads", description: "Google Guaranteed badge at the very top of search" },
  { to: "/services/social-media", label: "Social Media Advertising", description: "Targeted Meta & multi-channel client acquisition" },
  { to: "/services/sales-funnels", label: "Sales Funnels", description: "Automated lead intake & high-converting pipelines" },
  { to: "/services/gmb-optimization", label: "Reputation Booster", description: "5-star Google review capture & profile authority" },
];

const workItems = [
  { to: "/live-websites", label: "Website Portfolio", description: "Interactive platforms on live custom domains" },
  { to: "/portfolio", label: "Creative Archive", description: "Branding, graphic design, media & visual library" },
  { to: "/case-studies", label: "Verified Case Studies", description: "Documented performance & system builds" },
];

const aboutItems = [
  { to: "/about", label: "Our Story", description: "Founder, vision, and Michigan craftsmanship" },
  { to: "/faq", label: "FAQ", description: "Clear answers to common client questions" },
  { to: "/blog", label: "Blog", description: "Actionable marketing & local SEO insights" },
];

// Hot services for mobile quick access
const hotServices = [
  { id: "ppc", title: "Google Call Ads" },
  { id: "local-service-ads", title: "Local Service Ads" },
  { id: "seo", title: "Local SEO" },
  { id: "web-design", title: "Custom Web OS" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileWebOpen, setIsMobileWebOpen] = useState(false);
  const [isMobileMarketingOpen, setIsMobileMarketingOpen] = useState(false);
  const [isMobileWorkOpen, setIsMobileWorkOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileWebOpen(false);
    setIsMobileMarketingOpen(false);
    setIsMobileWorkOpen(false);
    setIsMobileAboutOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  const isWebDesignActive =
    location.pathname === "/services/web-design" ||
    location.pathname === "/services/custom-business-solutions" ||
    location.pathname === "/services/seo" ||
    location.pathname === "/services/ecommerce" ||
    location.pathname === "/services/app-development";

  const isMarketingActive =
    location.pathname === "/services/ppc" ||
    location.pathname === "/services/local-service-ads" ||
    location.pathname === "/services/social-media" ||
    location.pathname === "/services/sales-funnels" ||
    location.pathname === "/services/gmb-optimization";

  const isWorkActive =
    location.pathname.startsWith("/live-websites") ||
    location.pathname === "/portfolio" ||
    location.pathname.startsWith("/case-studies") ||
    location.pathname.startsWith("/projects/") ||
    location.pathname.startsWith("/showcase/");

  const isAboutActive =
    location.pathname === "/about" ||
    location.pathname === "/faq" ||
    location.pathname === "/blog" ||
    location.pathname.startsWith("/blog/");

  return (
    <div className="sticky top-0 z-50">
      <AnnouncementBar />
      <header
        className={cn(
          "w-full transition-all duration-500 ease-out relative",
          isScrolled
            ? "backdrop-blur-xl bg-primary/95 shadow-xl shadow-primary/15 border-b border-primary-foreground/5"
            : "bg-primary"
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group relative">
              <img 
                src={logoWhite} 
                alt="Valentine Digital Marketing Agency" 
                className="h-12 md:h-14 w-auto max-w-[200px] md:max-w-[260px] drop-shadow-lg transition-all duration-300 group-hover:scale-[1.02] group-hover:drop-shadow-[0_0_25px_hsl(var(--accent)/0.4)]"
              />
              <div className="absolute inset-0 bg-accent/10 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
            </Link>

            {/* Desktop Navigation - 4 High-Intent Pillars + Contact */}
            <nav className="hidden lg:flex items-center gap-6">
              <Link
                to="/"
                className={cn(
                  "relative text-sm font-medium transition-colors hover:text-accent py-2",
                  isActive("/") ? "text-accent" : "text-primary-foreground/80",
                  "after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300",
                  isActive("/") ? "after:w-full" : "after:w-0 hover:after:w-full"
                )}
              >
                Home
              </Link>

              {/* 1. Web Design Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(
                    "flex items-center gap-1 text-sm font-medium transition-colors hover:text-accent outline-none",
                    isWebDesignActive ? "text-accent" : "text-primary-foreground/80"
                  )}
                >
                  Web Design
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 backdrop-blur-xl bg-card/95 border-border/50 shadow-xl shadow-accent/5">
                  {webDesignItems.map((item) => (
                    <DropdownMenuItem key={item.to} asChild className="transition-colors duration-200 hover:bg-accent/10 focus:bg-accent/10">
                      <Link to={item.to} className="cursor-pointer flex flex-col items-start py-2">
                        <span className="font-semibold text-sm">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* 2. Advertising & Marketing Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(
                    "flex items-center gap-1 text-sm font-medium transition-colors hover:text-accent outline-none",
                    isMarketingActive ? "text-accent" : "text-primary-foreground/80"
                  )}
                >
                  Advertising & Marketing
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 backdrop-blur-xl bg-card/95 border-border/50 shadow-xl shadow-accent/5">
                  {marketingItems.map((item) => (
                    <DropdownMenuItem key={item.to} asChild className="transition-colors duration-200 hover:bg-accent/10 focus:bg-accent/10">
                      <Link to={item.to} className="cursor-pointer flex flex-col items-start py-2">
                        <span className="font-semibold text-sm">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* 3. Live Work Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(
                    "flex items-center gap-1 text-sm font-medium transition-colors hover:text-accent outline-none",
                    isWorkActive ? "text-accent" : "text-primary-foreground/80"
                  )}
                >
                  Live Work
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 backdrop-blur-xl bg-card/95 border-border/50 shadow-xl shadow-accent/5">
                  {workItems.map((item) => (
                    <DropdownMenuItem key={item.to} asChild className="transition-colors duration-200 hover:bg-accent/10 focus:bg-accent/10">
                      <Link to={item.to} className="cursor-pointer flex flex-col items-start py-2">
                        <span className="font-semibold text-sm">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* 4. About Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(
                    "flex items-center gap-1 text-sm font-medium transition-colors hover:text-accent outline-none",
                    isAboutActive ? "text-accent" : "text-primary-foreground/80"
                  )}
                >
                  About
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60 backdrop-blur-xl bg-card/95 border-border/50 shadow-xl shadow-accent/5">
                  {aboutItems.map((item) => (
                    <DropdownMenuItem key={item.to} asChild className="transition-colors duration-200 hover:bg-accent/10 focus:bg-accent/10">
                      <Link to={item.to} className="cursor-pointer flex flex-col items-start py-2">
                        <span className="font-semibold text-sm">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/contact"
                className={cn(
                  "relative text-sm font-medium transition-colors hover:text-accent py-2",
                  isActive("/contact") ? "text-accent" : "text-primary-foreground/80",
                  "after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300",
                  isActive("/contact") ? "after:w-full" : "after:w-0 hover:after:w-full"
                )}
              >
                Contact
              </Link>
            </nav>

            {/* Desktop Action Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <ThemeToggle />
              <a 
                href="tel:9898432600" 
                className="flex items-center gap-2 text-sm font-semibold text-primary-foreground/90 hover:text-accent transition-all duration-300 group"
              >
                <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110 text-accent" />
                <span>(989) 843-2600</span>
              </a>
              <Button asChild variant="ghost" size="sm" className="border border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/portal">Client Portal</Link>
              </Button>
              <Button variant="glow" className="group shadow-lg shadow-accent/20" asChild>
                <Link to="/contact">
                  Book Strategy Call
                </Link>
              </Button>
            </div>

            {/* Mobile Call Button + Hamburger Toggle */}
            <div className="lg:hidden flex items-center gap-2">
              <a
                href="tel:9898432600"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-sm font-semibold shadow-lg shadow-accent/25 hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 group"
              >
                <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                <span>Call</span>
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-primary-foreground hover:text-accent hover:bg-primary-foreground/5 rounded-lg transition-all duration-300"
                aria-label="Toggle menu"
              >
                <div className="relative w-6 h-6">
                  <Menu className={cn("h-6 w-6 absolute inset-0 transition-all duration-300", isMobileMenuOpen ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100")} />
                  <X className={cn("h-6 w-6 absolute inset-0 transition-all duration-300", isMobileMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-75")} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Slide-Down Menu */}
        <div
          className={cn(
            "lg:hidden bg-primary/98 backdrop-blur-xl border-t border-primary-foreground/10 transition-all duration-300 ease-out origin-top",
            isMobileMenuOpen 
              ? "max-h-[85vh] opacity-100 overflow-y-auto scale-y-100 translate-y-0" 
              : "max-h-0 opacity-0 overflow-hidden scale-y-95 -translate-y-2"
          )}
        >
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1 pb-24">
            {/* Find Your Fit Quiz CTA */}
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsQuizOpen(true);
              }}
              className="flex items-center gap-3 px-4 py-3 mb-2 rounded-lg bg-accent/10 border border-accent/20 text-accent text-left w-full"
            >
              <Sparkles className="h-5 w-5" />
              <div>
                <span className="font-semibold text-sm">Not Sure What You Need?</span>
                <p className="text-xs text-accent/80">Take our 30-second platform quiz</p>
              </div>
              <ChevronRight className="h-5 w-5 ml-auto" />
            </button>

            {/* Quick Access Hot Services */}
            <div className="mb-2 pb-3 border-b border-primary-foreground/10">
              <div className="flex items-center gap-2 px-2 mb-2">
                <Flame className="h-4 w-4 text-accent" />
                <span className="text-xs font-semibold text-primary-foreground/50 uppercase tracking-wider">High Intent Services</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {hotServices.map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-foreground/5 hover:bg-primary-foreground/10 transition-colors"
                  >
                    <Flame className="h-3.5 w-3.5 text-accent/80" />
                    <span className="text-xs font-medium text-primary-foreground/90">{service.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/"
              className={cn(
                "text-base font-medium py-2.5 min-h-[44px] flex items-center transition-colors",
                isActive("/") ? "text-accent" : "text-primary-foreground/80"
              )}
            >
              Home
            </Link>

            {/* 1. Mobile Web Design Accordion */}
            <div>
              <button
                onClick={() => setIsMobileWebOpen(!isMobileWebOpen)}
                className={cn(
                  "flex items-center justify-between w-full text-base font-medium py-2.5 min-h-[44px] transition-colors",
                  isWebDesignActive ? "text-accent" : "text-primary-foreground/80"
                )}
              >
                Web Design
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-300",
                    isMobileWebOpen && "rotate-180"
                  )}
                />
              </button>
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isMobileWebOpen ? "max-h-[350px] opacity-100" : "max-h-0 opacity-0"
                )}
              >
                <div className="pl-4 flex flex-col gap-1 mt-1">
                  {webDesignItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "text-sm py-2 min-h-[40px] flex items-center transition-colors",
                        location.pathname === item.to ? "text-accent" : "text-primary-foreground/70"
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* 2. Mobile Advertising & Marketing Accordion */}
            <div>
              <button
                onClick={() => setIsMobileMarketingOpen(!isMobileMarketingOpen)}
                className={cn(
                  "flex items-center justify-between w-full text-base font-medium py-2.5 min-h-[44px] transition-colors",
                  isMarketingActive ? "text-accent" : "text-primary-foreground/80"
                )}
              >
                Advertising & Marketing
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-300",
                    isMobileMarketingOpen && "rotate-180"
                  )}
                />
              </button>
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isMobileMarketingOpen ? "max-h-[350px] opacity-100" : "max-h-0 opacity-0"
                )}
              >
                <div className="pl-4 flex flex-col gap-1 mt-1">
                  {marketingItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "text-sm py-2 min-h-[40px] flex items-center transition-colors",
                        location.pathname === item.to ? "text-accent" : "text-primary-foreground/70"
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* 3. Mobile Live Work Accordion */}
            <div>
              <button
                onClick={() => setIsMobileWorkOpen(!isMobileWorkOpen)}
                className={cn(
                  "flex items-center justify-between w-full text-base font-medium py-2.5 min-h-[44px] transition-colors",
                  isWorkActive ? "text-accent" : "text-primary-foreground/80"
                )}
              >
                Live Work
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-300",
                    isMobileWorkOpen && "rotate-180"
                  )}
                />
              </button>
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isMobileWorkOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
                )}
              >
                <div className="pl-4 flex flex-col gap-1 mt-1">
                  {workItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "text-sm py-2 min-h-[40px] flex items-center transition-colors",
                        location.pathname === item.to ? "text-accent" : "text-primary-foreground/70"
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* 4. Mobile About Accordion */}
            <div>
              <button
                onClick={() => setIsMobileAboutOpen(!isMobileAboutOpen)}
                className={cn(
                  "flex items-center justify-between w-full text-base font-medium py-2.5 min-h-[44px] transition-colors",
                  isAboutActive ? "text-accent" : "text-primary-foreground/80"
                )}
              >
                About
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-300",
                    isMobileAboutOpen && "rotate-180"
                  )}
                />
              </button>
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isMobileAboutOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
                )}
              >
                <div className="pl-4 flex flex-col gap-1 mt-1">
                  {aboutItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "text-sm py-2 min-h-[40px] flex items-center transition-colors",
                        location.pathname === item.to ? "text-accent" : "text-primary-foreground/70"
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/contact"
              className={cn(
                "text-base font-medium py-2.5 min-h-[44px] flex items-center transition-colors",
                isActive("/contact") ? "text-accent" : "text-primary-foreground/80"
              )}
            >
              Contact
            </Link>

            <div className="pt-4 border-t border-primary-foreground/20 mt-2">
              <a href="tel:9898432600" className="flex items-center gap-2 text-primary-foreground/90 font-semibold mb-4">
                <Phone className="h-4 w-4 text-accent" />
                <span>(989) 843-2600</span>
              </a>
              <div className="flex gap-2">
                <Button asChild variant="outline" className="flex-1 border-primary-foreground/30 text-primary-foreground">
                  <Link to="/portal">Client Portal</Link>
                </Button>
                <Button asChild variant="glow" className="flex-1">
                  <Link to="/contact">Book Strategy Call</Link>
                </Button>
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* ServiceFinder Quiz Modal */}
      <ServiceFinder isOpen={isQuizOpen} onClose={() => setIsQuizOpen(false)} />
    </div>
  );
};

export default Header;
